
import { ProductCardData, ProductCarouselData } from '../Components/ProductFamily/models';
import { ContactUsJsonData } from '../Components/ContactUs/models';
import { FooterNavigationData, FooterNavigationItem } from '../Components/Footer/models';
import { RelatedContentData } from '../Components/RelatedContent/models';
import payloadData from '../payload';
export type { ProductCardData, ProductCarouselData } from '../Components/ProductFamily/models';
export type { ContactUsJsonData } from '../Components/ContactUs/models';
export type { FooterNavigationData, FooterNavigationItem, CountrySelectorData } from '../Components/Footer/models';

export const extractNavigationFromConfig = () => {
  try {
    const primaryNavComponent = payloadData?.components?.find(c => c.primarynavigation);
    const staticUrlsComponent = payloadData?.components?.find(c => c.staticURL);
    const primaryNav = primaryNavComponent?.primarynavigation;
    const staticUrls = staticUrlsComponent?.staticURL;
    const clearIcon = primaryNav?.clearIcon;
    if (primaryNav?.navigationItems) {
      const navigationItems = Object.values(primaryNav.navigationItems);      
      return {
        navigationItems: navigationItems,
        searchConfig: {
          isDisplaySearch: primaryNav.isDisplaySearch === "true",
          searchTextLabel: primaryNav.searchTextLabel,
          searchUrl: staticUrls?.searchURL, 
          searchIcon: primaryNav.searchIcon
        },
        logoConfig: {
          // Use local logo path from payload, fallback to default
          logoReference: primaryNav.logoReference
        },
        clearIcon: clearIcon,
        countrySelectorUrl: primaryNav.countrySelectorUrl,
        svgCountryIcon: primaryNav.countryIcon
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting navigation from config:', error);
    return null;
  }
};
/**
 * Extract navigation data from payload (legacy support)
 */
export const extractNavigationFromPayload = () => {
  return {
    source: 'none',
    data: []
  };
};
/**
 * Get primary navigation data
 */
export const getPrimaryNavigationData = () => {
  const configNav = extractNavigationFromConfig();
  const arrowIconsComponent = payloadData?.components?.find(c => c.primarynavigation?.upArrowIcon || c.primarynavigation?.downArrowIcon);
  const globalUpArrowIcon = arrowIconsComponent?.primarynavigation?.upArrowIcon;
  const downArrowIcon = arrowIconsComponent?.primarynavigation?.downArrowIcon;
  if (configNav && configNav.navigationItems) {
    const mappedData = configNav.navigationItems.map((item: any) => ({
      title: item.label || '',
      url: item.destinationLink,
      target: item.target,
      menuLinks: [], // Simplified structure
      headerNavigation: item.headerNavigation,
      headernavigation: item.headernavigation,
      upArrowIcon: globalUpArrowIcon,
      downArrowIcon: downArrowIcon,
    }));
    return {
      source: 'config',
      data: mappedData,
      clearIcon: configNav.clearIcon,
      countrySelectorUrl: configNav.countrySelectorUrl,
      svgCountryIcon: configNav.svgCountryIcon
    };
  }
  return {
    source: 'payload',
    data: [],
    clearIcon: undefined,
    countrySelectorUrl: undefined,
    svgCountryIcon: undefined
  };
};
/**
 * Get search configuration from components-config.json
 */
export const getSearchConfig = () => {
  const configNav = extractNavigationFromConfig();
  if (configNav?.searchConfig) {
    return {
      isEnabled: !!configNav.searchConfig.isDisplaySearch,
      searchUrl: configNav.searchConfig.searchUrl,
      searchIcon: configNav.searchConfig.searchIcon
    };
  }
  return null;
};
/**
 * Get logo configuration
 */
export const getLogoConfig = () => {
  const configNav = extractNavigationFromConfig();
  
  if (configNav?.logoConfig) {
    return {
      logoReference: configNav.logoConfig.logoReference,
      logoAlt: 'GE HealthCare',
      logoLink: '/',
      logoTitle: 'GE HealthCare'
    };
  }
  return null;
};
/**
 * Get HighlightKeyStats data from JSON config
 */
export const getHighlightKeyStatsData = () => {
  try {
    const highlightKeyStatsComponent = payloadData.components.find(component => component.highlightkeystats);
    return highlightKeyStatsComponent?.highlightkeystats || null;
  } catch (error) {
    console.error('Error loading HighlightKeyStats data:', error);
    return null;
  }
};
/**
 * Get RelatedContent data from JSON config
 */
export const getRelatedContentData = (): RelatedContentData | null => {
  try {
    const relatedContentComponent = payloadData.components.find(component => component.relatedcontent);
    const relatedContent = relatedContentComponent?.relatedcontent;
    if (!relatedContent) return null;
    // Ensure manualContentItems conforms to RelatedContentData type
    if (relatedContent.manualContentItems) {
      const manualContentItems: { [key: string]: any } = {};
      for (const [key, item] of Object.entries(relatedContent.manualContentItems)) {
        if (item && typeof item === 'object') {
          manualContentItems[key] = {
            ...item,
            fileReference: 'fileReference' in item
              ? { ...(item as { fileReference: object }).fileReference }
              : undefined,
          };
        }
      }
      return {
        ...relatedContent,
        manualContentItems
      } as RelatedContentData;
    }
    return relatedContent as RelatedContentData;
  } catch (error) {
    console.error('Error loading RelatedContent data:', error);
    return null;
  }
};
/**
 * Get product carousel data from payloadData
 */
export const getProductCarouselData = (): ProductCarouselData => {
  const productCarouselComponent = payloadData.components.find(component => {
    const containerKey = Object.keys(component).find(k => k.startsWith('carousel-container'));
    return containerKey && Object.keys(component[containerKey]).some((key: string) => key.startsWith('productcardcarousel'));
  });
  if (productCarouselComponent) {
    const containerKey = Object.keys(productCarouselComponent).find(k => k.startsWith('carousel-container'));
    const container = productCarouselComponent[containerKey];
    const filtered: ProductCarouselData = {};
    for (const [key, value] of Object.entries(container)) {
      if (
        key.startsWith('productcardcarousel') &&
        value &&
        typeof value === 'object' &&
        'fileReference' in value &&
        (value as any).fileReference?.assetName) {
        filtered[key] = value as ProductCardData;
      }
    }
    return filtered;
  }
  return {};
};
/**
 * Get product carousel title from JSON config
 */
export const getProductCarouselTitle = (): string => {
  // Find the product carousel container in the components config
  const productCarouselComponent = payloadData.components.find(component => {
    const containerKey = Object.keys(component).find(k => k.startsWith('carousel-container'));
    return containerKey && Object.keys(component[containerKey]).some((key: string) => key.startsWith('productcardcarousel'));
  });
  if (productCarouselComponent) {
    const containerKey = Object.keys(productCarouselComponent).find(k => k.startsWith('carousel-container'))!;
    return productCarouselComponent[containerKey].productCardTitle || '';
  }
  return '';
};
/**
 * Get product carousel data as array
 */
export const getProductCarouselArray = (): ProductCardData[] => {
  const carouselData = getProductCarouselData();
  const arrayData = Object.values(carouselData) as ProductCardData[];
  return arrayData;
};
/**
 * Get ContactUs data from JSON config
 */
export const getContactUsData = (): ContactUsJsonData | null => {
  // Find the contactus component in the components config
  const contactUsComponent = payloadData.components.find(
    component => component.contactus
  );
  if (contactUsComponent && contactUsComponent.contactus) {
    return contactUsComponent.contactus as ContactUsJsonData;
  }
  return null;
};
/**
 * Get footer navigation data from JSON config
 */
export const getFooterNavigationData = (): FooterNavigationData | null => {
  // Find the footer navigation component in the components config
  const footerComponent = payloadData.components.find(
    component => component.footernavigation
  );
  // Sitemap/country-selector URL comes from primary navigation
  const primaryNavComponent = payloadData?.components?.find(c => c.primarynavigation);
  const primaryNav = primaryNavComponent?.primarynavigation;
  if (footerComponent && footerComponent.footernavigation) {
    const footerData = footerComponent.footernavigation;
    
    // Transform the footer navigation items from object format to array format
    const footerNavigationItems: FooterNavigationItem[] = [];
    
    if (footerData.footerNavigationItems) {
      for (const key of Object.keys(footerData.footerNavigationItems)) {
        const item = footerData.footerNavigationItems[key];
        footerNavigationItems.push({
          path: item.path,
          textLabel: item.textLabel,
          urlTarget: item.urlTarget
        });
      }
    }
    return {
      id: 'footer-navigation',
      linkLogoImageToHomepage: footerData.isLinkLogoImageToHomepage === 'true',
      logoImageHomepageLink: '/', // Default home page link
      // Always use local logo path for Footer
      logoReference: footerData.logoReference,
      footerNavigationItems,
      topLeftColumnText: footerData.topLeftColumnText,
      topRightColumnText: footerData.topRightColumnText,
      bottomLeftColumnText: footerData.bottomLeftColumnText,
      bottomRightColumnText: footerData.bottomRightColumnText,
      cookiePreferencesText: footerData.cookiePreferencesText || '',
      countrySelector: {
        countryIcon: (footerData as any).countryselector ? (footerData as any).countryselector.countryIcon : undefined,
        caretIcon: (footerData as any).countryselector ? (footerData as any).countryselector.caretIcon : undefined,
        currentCountry: 'China', // Default or could be dynamic
        countryUrl: primaryNav?.countrySelectorUrl
      }
    } as FooterNavigationData;
  }
  return null;
};

/**
 * Get homepage hero carousel data from JSON config
 */
export const getHomepageHeroCarouselData = () => {
  try {
    // Find the homepage hero carousel container in the components config
    const heroCarouselComponent = payloadData.components.find(component => {
      const containerKey = Object.keys(component).find(k => k.startsWith('carousel-container'));
      return containerKey && component[containerKey].carouselVariationClass === 'ge-homepage-hero-carousel';
    });
    if (heroCarouselComponent) {
      const containerKey = Object.keys(heroCarouselComponent).find(k => k.startsWith('carousel-container'));
      const container = heroCarouselComponent[containerKey];
      // Collect all homepageherocarousel-* items as an array
      const slides = Object.keys(container)
        .filter(key => key.startsWith('homepageherocarousel-'))
        .map(key => container[key]);
      return {
        ...container,
        slides
      };
    }
    return null;
  } catch (error) {
    console.error('Error extracting homepage hero carousel from config:', error);
    return null;
  }
};

export const getAnalytics = () => {
  const analyticsComponent = payloadData?.components?.find(c => c.analyticsData);
  const analyticsData = analyticsComponent?.analyticsData;
  return analyticsData ? { ...analyticsData } : {};
}

/**
 * Utility function to strip HTML tags
 */
export const stripHtmlTags = (html: string): string => {
  return html.replaceAll(/<[^>]*>/g, '').replaceAll('\r\n', '');
};